import React, {useEffect, useMemo, useState} from "react";
import {Layout} from "../../pages/siren/Layout";
import {useSiren} from "../../pages/siren/SirenContext";
import {CountryAvailability, getCountryAvailability} from "./hooks/useGetCountryAvailability";
import {countries} from "../../lib/countries/data";

const countryMap = new Map(countries.map(c => [c.countryCode, c]));

function SupportedBadge({supported}: {supported: boolean}) {
    if (supported) {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-desaturated-green text-dark-green">
                ✓ Supported
            </span>
        );
    }
    return <span className="text-sm text-light-grey">—</span>;
}

export default function SirenCountriesPage() {
    const {clientID} = useSiren();
    const [rows, setRows] = useState<CountryAvailability[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getCountryAvailability({clientID: clientID ?? undefined}).then(data => {
            setRows(data);
            setLoading(false);
        });
    }, [clientID]);

    const enriched = useMemo(() =>
        rows.map(r => ({...r, country: countryMap.get(r.countryCode)}))
            .sort((a, b) => {
                const nameA = a.country?.countryNameEn ?? a.countryCode;
                const nameB = b.country?.countryNameEn ?? b.countryCode;
                return nameA.localeCompare(nameB);
            }),
        [rows]
    );

    return (
        <Layout menuCurrent="countries">
            <div className="p-6">
                <div className="flex items-center gap-3 mb-5">
                    <h1 className="text-xl font-semibold text-dark-dark-grey">Country Availability</h1>
                    {!loading && (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-light-light-grey text-dark-grey">
                            {enriched.length} {enriched.length === 1 ? "country" : "countries"}
                        </span>
                    )}
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-light-grey text-sm">Loading...</div>
                    ) : enriched.length === 0 ? (
                        <div className="p-8 text-center text-light-grey text-sm">
                            No countries configured for this client.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-light-light-grey bg-desaturated-grey">
                                    <th className="py-3 px-5 text-left font-medium text-dark-grey text-xs uppercase tracking-wide">
                                        Country
                                    </th>
                                    <th className="py-3 px-5 text-center font-medium text-dark-grey text-xs uppercase tracking-wide w-36">
                                        SMS
                                    </th>
                                    <th className="py-3 px-5 text-center font-medium text-dark-grey text-xs uppercase tracking-wide w-36">
                                        Phone Call
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {enriched.map((r, i) => (
                                    <tr
                                        key={r.countryCode}
                                        className={`border-b border-light-light-grey last:border-0 hover:bg-desaturated-grey transition-colors ${i % 2 === 0 ? "" : "bg-[#fafafa]"}`}
                                    >
                                        <td className="py-3 px-5">
                                            <div className="flex items-center gap-3">
                                                <span className="text-xl leading-none">{r.country?.flag ?? "🌐"}</span>
                                                <div>
                                                    <div className="font-medium text-dark-dark-grey">
                                                        {r.country?.countryNameEn ?? r.countryCode}
                                                    </div>
                                                    <div className="text-xs text-light-grey font-mono mt-0.5">
                                                        {r.countryCode}
                                                        {r.country?.countryCallingCode && ` · +${r.country.countryCallingCode}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-5 text-center">
                                            <SupportedBadge supported={r.sms}/>
                                        </td>
                                        <td className="py-3 px-5 text-center">
                                            <SupportedBadge supported={r.call}/>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </Layout>
    );
}
